import { Component } from '@angular/core';
import { TranslateService } from "@ngx-translate/core";
import { LocalStorageService, SessionStorageService } from "ngx-webstorage";
import { environment } from '../environments/environment';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { AuthService } from './services/auth.service';
import { StorageUrlService } from './services/storage-url.service';
import { SupportChatService } from './services/support-chat.service';
import { GebruikMeldingService } from './services/gebruik-melding.service';
import { isVerlopen, KIJK_ELKE_MS } from './helpers/sessie';
import { logoutUserAndRedirect } from './interceptors/error.interceptor';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent {

  constructor(private translateService: TranslateService,
              private localStorageService: LocalStorageService,
              private router: Router,
              private activatedRoute: ActivatedRoute,
              private titleService: Title,
              private bsLocaleService: BsLocaleService,
              private authService: AuthService,
              private storageUrlService: StorageUrlService,
              private supportChat: SupportChatService,
              private gebruikMelding: GebruikMeldingService,
              private sessionStorageService: SessionStorageService) {

    this.setTitle();
    this.setFavicon();

    // Een teruggekeerde bezoeker heeft nog een geldig token in de opslag van
    // de browser, zonder zojuist te hebben ingelogd — dat is verder het
    // enige moment waarop iemand beeld te zien krijgt zonder dat
    // AuthService.bewaarSessie() net is gedraaid.
    //
    // Open-als-club: geen vernieuwing hier. Die route zet eerst het nieuwe
    // token en herlaadt daarna; een parallelle Refresh op de oude sessie
    // zou het nieuwe token overschrijven.
    const openAlsClub = typeof window !== 'undefined'
      && window.location.pathname.includes('/auth/open-als-club');
    if (this.authService.token && !openAlsClub) {
      this.storageUrlService.refresh();
      this.vernieuwRechten();
    }
    this.bewaakSessie();

    const lang = this.localStorageService.retrieve('language') ?? environment.defaultLanguage;
    this.translateService.use(lang);
    this.bsLocaleService.use(lang);

    this.localStorageService.observe('language').subscribe(value => {
      this.translateService.use(value);
      this.bsLocaleService.use(value);
    });

    this.loadCrispChat();

    // Weekoverzicht in de beheerconsole: welk onderdeel staat open.
    this.gebruikMelding.start();

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {

        // Check if 'hideCrisp' is set to true in route data
        if (this.activatedRoute.firstChild.firstChild.snapshot.data['hideCrisp']) {
          this.hideCrispChat();
        } else {
          this.showCrispChat();
        }
      }
    });
  }

  /**
   * Bij het opstarten het token vernieuwen, zodat nieuwe rechten (zoals
   * "live meekijken") niet op een nieuwe inlog hoeven te wachten. Zijn de
   * rechten veranderd, dan één keer herladen: de schermen lezen de
   * rechtenlijst bij het opbouwen. Mislukt het (verlopen token, studio-
   * token), dan gebeurt er niets; de bestaande afhandeling doet de rest.
   */
  private vernieuwRechten(): void {
    this.authService.vernieuwToken().subscribe({
      next: (veranderd) => {
        if (veranderd) {
          window.location.reload();
        }
      },
      error: () => {},
    });
  }

  /**
   * Het token leeft 24 uur en wordt alleen hierboven, bij het opstarten,
   * vernieuwd. Een tabblad dat langer openstond kreeg daarna overal 401 en
   * toonde lege pagina's (21-10-2026). Is de sessie verlopen, dan nu meteen
   * naar het inlogscherm, en niet pas bij de volgende klik (eigenaar
   * 21-10-2026: "we moeten hem toch uitloggen"): elke minuut, en zodra het
   * tabblad weer in beeld komt. Na het inloggen komt hij terug op dezelfde
   * pagina (helpers/sessie.ts).
   */
  private bewaakSessie(): void {
    const kijk = () => {
      const openAlsClub = window.location.pathname.includes('/auth/open-als-club');
      if (!openAlsClub && isVerlopen(this.authService.token)) {
        logoutUserAndRedirect(this.authService, this.sessionStorageService, this.router);
      }
    };
    setInterval(kijk, KIJK_ELKE_MS);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        kijk();
      }
    });
  }

  // Telefoonbreedte (zelfde grens als style/components/_mobile.scss).
  private opTelefoon(): boolean {
    return typeof window.matchMedia === 'function'
      && window.matchMedia('(max-width: 767.98px)').matches;
  }

  loadCrispChat(): void {
    // Op een telefoon laden we de supportchat helemaal niet: de knop hangt
    // over de tabbalk onderaan en het script weegt onnodig mee (12-09-2026).
    if (this.opTelefoon()) {
      return;
    }
    const d = document;
    const s = d.createElement('script');
    s.src = 'https://client.crisp.chat/l.js';
    s.async = true;
    d.getElementsByTagName('head')[0].appendChild(s);

    // Crisp website ID (replace with your own Crisp website ID)
    window.$crisp = [];
    // Op een telefoon hangt de chatknop over de tabbalk onderaan; daar dus
    // niet tonen (zie ook style/components/_mobile.scss als vangnet).
    window.$crisp.push(["config", "hide:on:mobile", [true]]);
    window.CRISP_WEBSITE_ID = environment.cripsWebsiteID; // Replace this with your Crisp website ID
    // Geen zwevende knop rechtsonder: de chat opent via de knop in de
    // koptekst (SupportChatService), zodat hij niet over de pagina hangt.
    this.supportChat.verbergStandaardKnop();
  }

  private setTitle() {
    this.titleService.setTitle(environment.appTitle);
  }

  private setFavicon() {
    const faviconPath = environment.faviconPath;
    const link: HTMLLinkElement | null = document.querySelector('#favicon');

    if (link) {
      link.href = faviconPath;
    }
  }

  /** Route met `hideCrisp`: chat dicht én de knop in de koptekst weg. */
  hideCrispChat(): void {
    this.supportChat.beschikbaar.set(false);
    if (window.$crisp) {
      window.$crisp.push(["safe", true])
      window.$crisp.push(["do", "chat:hide"]);
    }
  }

  /** Gewone route: alleen de knop in de koptekst tonen; de box blijft dicht tot iemand drukt. */
  showCrispChat(): void {
    this.supportChat.beschikbaar.set(!!window.$crisp && !this.opTelefoon());
  }
}
